import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ImageType } from "../../../types/productTypes";
import { useAdminStore } from "../../../store/useAdminStore";
import { LogoIcon } from "@/assets/icons/logo";
import { useNavigate } from "react-router-dom";
const imageCache = new Map<string, HTMLImageElement>();
const compositeCache = new Map<string, string>();
let lastCanvasSize = { width: 0, height: 0 };

type NormalGalleryProps = {
   itemPairImages: ImageType[][] | null | undefined;
   stageName: string;
   mainImage: ImageType | undefined;
   productTitle: string;
};

export default function NormalGallery({
   itemPairImages,
   stageName,
   mainImage,
   productTitle,
}: NormalGalleryProps) {
   const [composites, setComposites] = useState<{
      previous: string | null;
      current: string | null;
   }>({ previous: null, current: null });
   const mergeAbort = useRef<AbortController>();

   // Remove container size tracking since we don't need it anymore
   const containerRef = useRef<HTMLDivElement>(null);
   const { setPdfImages } = useAdminStore();
   const navigate = useNavigate();
   useEffect(() => {
      const urls = flattenImageUrls(itemPairImages);
      if (mainImage?.url) {
         urls.unshift(mainImage.url);
      }
      if (urls.length === 0) {
         setComposites({ previous: null, current: null });
         return;
      }

      // Create cache key from URLs and stage
      const cacheKey = `${stageName}-${urls.join("|")}`;

      // Use cached composite if available and canvas size hasn't changed
      if (compositeCache.has(cacheKey)) {
         setComposites((prev) => ({
            previous: prev.current,
            current: compositeCache.get(cacheKey)!,
         }));
         if (stageName === "Furniture") {
            console.log("Setting PDF image for vidus");
            setPdfImages("vidus", compositeCache.get(cacheKey)!);
         } else if (stageName === "Windows") {
            console.log("Setting PDF image for fasadas");
            setPdfImages("fasadas", compositeCache.get(cacheKey)!);
         }
         return;
      }

      if (mergeAbort.current) {
         mergeAbort.current.abort();
      }
      const controller = new AbortController();
      mergeAbort.current = controller;

      // Batch the state updates
      let batchedComposite: string | null = null;

      // Fast path: check if all images are already cached
      const allCached = urls.every((url) => imageCache.has(url));
      if (allCached) {
         batchedComposite = mergeImagesSync(urls);
         if (batchedComposite) {
            compositeCache.set(cacheKey, batchedComposite);
            setComposites((prev) => ({
               previous: prev.current,
               current: batchedComposite,
            }));
            if (stageName === "Furniture") {
               console.log("Setting PDF image for vidus");
               setPdfImages("vidus", batchedComposite);
            } else if (stageName === "Windows") {
               console.log("Setting PDF image for fasadas");
               setPdfImages("fasadas", batchedComposite);
            }
            return;
         }
      }

      // Slow path: need to preload images
      preloadImages(urls)
         .then(() => {
            if (controller.signal.aborted) return null;
            return mergeImagesSync(urls);
         })
         .then((newComposite) => {
            if (!controller.signal.aborted && newComposite) {
               compositeCache.set(cacheKey, newComposite);
               setComposites((prev) => ({
                  previous: prev.current,
                  current: newComposite,
               }));
               if (stageName === "Furniture") {
                  console.log("Setting PDF image for vidus");
                  setPdfImages("vidus", newComposite);
               } else if (stageName === "Windows") {
                  console.log("Setting PDF image for fasadas");
                  setPdfImages("fasadas", newComposite);
               }
            }
         })
         .catch((err) => {
            if (err.name !== "AbortError") {
               console.error("Error merging images:", err);
               setComposites({ previous: null, current: null });
            }
         });

      return () => controller.abort();
   }, [itemPairImages, stageName]);

   return (
      <div
         ref={containerRef}
         className="relative w-full h-full overflow-hidden"
         style={{ minHeight: "200px" }}
      >
         {mainImage?.url && (
            <div
               className="absolute inset-0 bg-no-repeat bg-center z-0"
               style={{
                  backgroundImage: `url(${mainImage.url})`,
                  backgroundSize: "contain",
               }}
            />
         )}
         <div className="absolute top-0 z-50 left-0 w-full h-full flex pl-10 pt-4 gap-5">
            <LogoIcon
               className="cursor-pointer"
               onClick={() => navigate("/")}
            />
            <div
               className="h-8 text-custom-dark border 
                    stroke-custom-dark border-custom-dark"
            ></div>
            <h3 className="text-d-h3 font-semibold">{productTitle}</h3>
         </div>

         <AnimatePresence>
            {composites.previous &&
               composites.previous !== composites.current && (
                  <motion.div
                     key={`previous-${composites.previous}`}
                     className="absolute inset-0 bg-no-repeat bg-center z-10"
                     style={{
                        backgroundImage: `url(${composites.previous})`,
                        backgroundSize: "contain",
                     }}
                     initial={{ opacity: 1 }}
                     animate={{ opacity: 0 }}
                     exit={{ opacity: 0 }}
                     transition={{ duration: 0.5, delay: 0.5 }}
                  />
               )}

            {composites.current && (
               <motion.div
                  key={`current-${composites.current}`}
                  className="absolute inset-0 bg-no-repeat bg-center z-20"
                  style={{
                     backgroundImage: `url(${composites.current})`,
                     backgroundSize: "contain",
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
               />
            )}
         </AnimatePresence>
      </div>
   );
}

/**
 * Preload images and cache them
 */
async function preloadImages(urls: string[]): Promise<void> {
   const loadPromises = urls.map((url) => {
      if (imageCache.has(url)) {
         return Promise.resolve(imageCache.get(url)!);
      }
      return new Promise<HTMLImageElement>((resolve, reject) => {
         const img = new Image();
         img.crossOrigin = "anonymous";
         img.onload = () => {
            imageCache.set(url, img);
            resolve(img);
         };
         img.onerror = reject;
         img.src = url;
      });
   });

   await Promise.all(loadPromises);
}

/**
 * Synchronous merge for cached images
 */
function mergeImagesSync(urls: string[]): string | null {
   try {
      const images = urls.map((url) => imageCache.get(url)!);
      const firstImage = images[0];

      // Reuse canvas if size hasn't changed
      if (
         !lastCanvasSize.width ||
         lastCanvasSize.width !== firstImage.width ||
         lastCanvasSize.height !== firstImage.height
      ) {
         lastCanvasSize = {
            width: firstImage.width,
            height: firstImage.height,
         };
      }

      const canvas = document.createElement("canvas");
      canvas.width = lastCanvasSize.width;
      canvas.height = lastCanvasSize.height;

      const ctx = canvas.getContext("2d", {
         willReadFrequently: true,
         alpha: false, // Optimization when we know we'll paint the entire canvas
      });
      if (!ctx) return null;

      // Draw all images in one batch
      images.forEach((img) => {
         ctx.drawImage(img, 0, 0);
      });

      return canvas.toDataURL("image/jpeg", 0.85); // JPEG is faster than PNG
   } catch (error) {
      console.error("Error in sync merge:", error);
      return null;
   }
}

/**
 * Flattens the nested image arrays into an array of image URLs.
 */
function flattenImageUrls(
   itemPairImages: ImageType[][] | null | undefined
): string[] {
   return (
      itemPairImages
         ?.flatMap((pair) =>
            pair.map((img) => {
               // Remove 'Select' or 'select' from image names if present
               if (img.name) {
                  img.name = img.name.replace(/\b[Ss]elect\b/g, "").trim();
               }
               return img.url;
            })
         )
         .filter(Boolean) || []
   );
}
